import { Box, useTheme } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useScreenOrientation } from '@hooks/useScreenOrientation';
import { ClickableBox } from '@uiKit/ClickableBox';

import { IThumbnail, IThumbnailId } from '../../types';

interface ThumbnailProps {
  id: IThumbnailId;
  index: number;
  order: number;
  isActive: boolean;
  image: string;
  croppedImage: string;
  thumbnailImage: string;
  onClickThumbnail: (props: IThumbnail) => any;
  isDragDisabled: boolean;
}

const DEFAULT_BORDER_RADIUS = 10;

export const Thumbnail = ({
  id,
  isActive,
  image,
  order,
  onClickThumbnail,
  croppedImage,
  thumbnailImage,
}: ThumbnailProps) => {
  const theme = useTheme();
  const orientation = useScreenOrientation();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const getDefaultStyles = () => ({
    borderRadius: `${DEFAULT_BORDER_RADIUS}px`,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        position: 'relative',
        border: isActive ? `2px solid ${theme.palette.primary.main}` : 'none',
        height: orientation === 'landscape' ? 'auto' : '100%',
        width: orientation === 'landscape' ? '100%' : 'auto',
        backgroundColor: 'black',
        ...style,
      }}
    >
      <ClickableBox
        onClick={() => onClickThumbnail({ id: id as number, image, order, croppedImage, thumbnailImage })}
        disabled={typeof id === 'string'}
        aria-label="Select thumbnail"
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          transition: '.2s',
          cursor: 'pointer',
          '&:hover': { transition: '.2s', bgcolor: 'rgba(0, 0, 0, 0.5)' },
          ...getDefaultStyles(),
        }}
      />

      <img
        style={{
          width: orientation === 'landscape' ? '100%' : 'auto',
          height: orientation === 'landscape' ? 'auto' : '100%',
          maxWidth: 'unset',
          ...getDefaultStyles(),
        }}
        src={thumbnailImage || croppedImage || image}
        alt="thumbnail"
      />
    </Box>
  );
};
